import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ThemeProvider } from "@/components/ThemeProvider";
import { AuthProvider } from "@/context/AuthContext";
import { WorkspaceProvider } from "@/context/WorkspaceContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { pingWarm } from "@/lib/api";
import { captureUtm } from "@/lib/utm";
import { Toaster } from "@/components/ui/sonner";
import Landing from "@/pages/Landing";
import Demo from "@/pages/Demo";
import Product from "@/pages/Product";
import IndustrySolution from "@/pages/IndustrySolution";
import LeadConfirm from "@/pages/LeadConfirm";
import Connectors from "@/pages/Connectors";
import RoadmapPage from "@/pages/RoadmapPage";
import About from "@/pages/About";
import Customers from "@/pages/Customers";
import Careers from "@/pages/Careers";
import Changelog from "@/pages/Changelog";
import Trust from "@/pages/Trust";
import Status from "@/pages/Status";
import Contact from "@/pages/Contact";
import FeaturePage from "@/pages/FeaturePage";
import LegalPage from "@/pages/LegalPage";
import Signup from "@/pages/Signup";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Schema from "@/pages/Schema";
import Glossary from "@/pages/Glossary";
import Relationships from "@/pages/Relationships";
import Team from "@/pages/Team";
import Chat from "@/pages/Chat";
import AskPreview from "@/pages/AskPreview";
import Reports from "@/pages/Reports";
import ReportDetail from "@/pages/ReportDetail";
import Alerts from "@/pages/Alerts";
import AlertDetail from "@/pages/AlertDetail";
import SignalsQueue from "@/pages/SignalsQueue";
import SignalsRegistry from "@/pages/SignalsRegistry";
import RegistryEntry from "@/pages/RegistryEntry";
import Semantics from "@/pages/Semantics";
import SemanticsMetrics from "@/pages/SemanticsMetrics";
import SemanticsPacks from "@/pages/SemanticsPacks";
import MetricDetail from "@/pages/MetricDetail";
import SettingsWorkspace from "@/pages/SettingsWorkspace";
import SettingsAudit from "@/pages/SettingsAudit";
import SettingsBilling from "@/pages/SettingsBilling";
import MagicCallback from "@/pages/MagicCallback";
import VerifyEmail from "@/pages/VerifyEmail";
import Onboarding from "@/pages/Onboarding";
import Health from "@/pages/Health";
import Docs from "@/pages/Docs";
import Boards from "@/pages/Boards";
import BoardDetail from "@/pages/BoardDetail";
import PublicBoard from "@/pages/PublicBoard";
import Presentations from "@/pages/Presentations";
import PresentationDetail from "@/pages/PresentationDetail";
import PublicDeck from "@/pages/PublicDeck";
import Blog from "@/pages/Blog";
import ArticlePage from "@/pages/ArticlePage";
import NotFound from "@/pages/NotFound";
import Checks from "@/pages/Checks";
import SurveyReadiness from "@/pages/SurveyReadiness";
import EvidenceShareView from "@/pages/EvidenceShareView";
import { CommandPalette } from "@/components/CommandPalette";

const queryClient = new QueryClient();

function App() {
  // Wake the backend on first load (so the embedding model warms up while the user
  // navigates/logs in) and keep the scale-to-zero container alive during an active
  // session with a tiny periodic ping — so even the very first query feels instant.
  useEffect(() => {
    captureUtm();
    pingWarm();
    const id = setInterval(() => pingWarm(), 4 * 60 * 1000);
    return () => clearInterval(id);
  }, []);
  return (
    <HelmetProvider>
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <WorkspaceProvider>
            <div className="App">
              <BrowserRouter>
                <Routes>
                  <Route path="/" element={<Landing />} />
                  <Route path="/demo" element={<Demo />} />
                  <Route path="/product" element={<Product />} />
                  <Route path="/solutions/:industry" element={<IndustrySolution />} />
                  <Route path="/lead/confirm" element={<LeadConfirm />} />
                  <Route path="/connectors" element={<Connectors />} />
                  <Route path="/discovery" element={<FeaturePage slug="discovery" />} />
                  <Route path="/governance" element={<FeaturePage slug="governance" />} />
                  <Route path="/roadmap" element={<RoadmapPage />} />
                  <Route path="/about" element={<About />} />
                  <Route path="/customers" element={<Customers />} />
                  <Route path="/careers" element={<Careers />} />
                  <Route path="/contact" element={<Contact />} />
                  <Route path="/changelog" element={<Changelog />} />
                  <Route path="/trust" element={<Trust />} />
                  <Route path="/status" element={<Status />} />
                  <Route path="/privacy" element={<LegalPage slug="privacy" />} />
                  <Route path="/terms" element={<LegalPage slug="terms" />} />
                  <Route path="/dpdp" element={<LegalPage slug="dpdp" />} />
                  <Route path="/data-privacy" element={<LegalPage slug="data-privacy" />} />
                  <Route path="/eula" element={<LegalPage slug="eula" />} />
                  <Route path="/dpa" element={<LegalPage slug="dpa" />} />
                  <Route path="/subprocessors" element={<LegalPage slug="subprocessors" />} />
                  <Route path="/signup" element={<Signup />} />
                  <Route path="/login" element={<Login />} />
                  <Route path="/auth/magic" element={<MagicCallback />} />
                  <Route path="/verify-email" element={<VerifyEmail />} />
                  <Route path="/docs" element={<Docs />} />
                  <Route path="/docs/:slug" element={<Docs />} />
                  <Route path="/blog" element={<Blog />} />
                  <Route path="/blog/:slug" element={<ArticlePage />} />
                  <Route path="/compare/:slug" element={<ArticlePage />} />
                  <Route path="/semantic-layer" element={<ArticlePage />} />
                  <Route path="/b/:token" element={<PublicBoard />} />
                  <Route path="/deck/:token" element={<PublicDeck />} />
                  <Route path="/e/:token" element={<EvidenceShareView />} />
                  <Route
                    path="/health"
                    element={
                      <ProtectedRoute>
                        <Health />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/onboarding"
                    element={
                      <ProtectedRoute>
                        <Onboarding />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/dashboard"
                    element={
                      <ProtectedRoute>
                        <Dashboard />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/schema"
                    element={
                      <ProtectedRoute>
                        <Schema />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/glossary"
                    element={
                      <ProtectedRoute>
                        <Glossary />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/graph"
                    element={
                      <ProtectedRoute>
                        <Relationships />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/ask"
                    element={
                      <ProtectedRoute>
                        <AskPreview />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/boards"
                    element={
                      <ProtectedRoute>
                        <Boards />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/boards/:id"
                    element={
                      <ProtectedRoute>
                        <BoardDetail />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/present"
                    element={
                      <ProtectedRoute>
                        <Presentations />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/present/:id"
                    element={
                      <ProtectedRoute>
                        <PresentationDetail />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/team"
                    element={
                      <ProtectedRoute>
                        <Team />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/settings/workspace"
                    element={
                      <ProtectedRoute>
                        <SettingsWorkspace />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/settings/audit"
                    element={
                      <ProtectedRoute>
                        <SettingsAudit />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/settings/billing"
                    element={
                      <ProtectedRoute>
                        <SettingsBilling />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/chat"
                    element={
                      <ProtectedRoute>
                        <Chat />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/reports"
                    element={
                      <ProtectedRoute>
                        <Reports />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/reports/:id"
                    element={
                      <ProtectedRoute>
                        <ReportDetail />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/alerts"
                    element={
                      <ProtectedRoute>
                        <Alerts />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/alerts/:id"
                    element={
                      <ProtectedRoute>
                        <AlertDetail />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/signals/queue"
                    element={
                      <ProtectedRoute>
                        <SignalsQueue />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/signals/registry"
                    element={
                      <ProtectedRoute>
                        <SignalsRegistry />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/signals/registry/:id"
                    element={
                      <ProtectedRoute>
                        <RegistryEntry />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/signals"
                    element={
                      <ProtectedRoute>
                        <SignalsQueue />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/semantics"
                    element={
                      <ProtectedRoute>
                        <Semantics />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/semantics/metrics"
                    element={
                      <ProtectedRoute>
                        <SemanticsMetrics />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/semantics/packs"
                    element={
                      <ProtectedRoute>
                        <SemanticsPacks />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/semantics/metrics/:id"
                    element={
                      <ProtectedRoute>
                        <MetricDetail />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/checks"
                    element={
                      <ProtectedRoute>
                        <Checks />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/survey-readiness"
                    element={
                      <ProtectedRoute>
                        <SurveyReadiness />
                      </ProtectedRoute>
                    }
                  />
                  <Route path="*" element={<NotFound />} />
                </Routes>
                <CommandPalette />
              </BrowserRouter>
              <Toaster />
            </div>
          </WorkspaceProvider>
        </AuthProvider>
      </QueryClientProvider>
    </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;
